import { formatMoney } from 'common/src/other/currencyFormat';
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    IconButton,
    Tooltip
} from '@mui/material';
import RestartAltRoundedIcon from '@mui/icons-material/RestartAltRounded';
import C2Dialog, { C2DangerButton, C2SecondaryButton } from '../components/C2Dialog';
import {
    GoogleMap,
    Marker,
    InfoWindow
} from "@react-google-maps/api";
import { useSelector, useDispatch } from "react-redux";
import CircularLoading from "../components/CircularLoading";
import { useTranslation } from "react-i18next";
import Chart from 'react-apexcharts';
import Grid from '@mui/material/Grid';
import TodayOutlinedIcon from '@mui/icons-material/TodayOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import AccountBalanceWalletOutlinedIcon from '@mui/icons-material/AccountBalanceWalletOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import WifiTetheringOutlinedIcon from '@mui/icons-material/WifiTetheringOutlined';
import NavigationOutlinedIcon from '@mui/icons-material/NavigationOutlined';
import MapOutlinedIcon from '@mui/icons-material/MapOutlined';
import InsertChartOutlinedIcon from '@mui/icons-material/InsertChartOutlined';
import { api } from 'common';

const C2_FONT = "'Outfit', 'Poppins', sans-serif";
const C2 = {
    sidebar: '#1A0F3B',
    dark: '#221448',
    deep: '#2C195B',
    mid: '#3B2275',
    violet: '#6411ED',
    bright: '#7628FF',
    white: '#FBFAFE',
    lavender: '#EBE7FB',
    border: '#D7CFEB',
    muted: '#766293',
};
const DEFAULT_MAP_CENTER = { lat: 4.711, lng: -74.0721 };
const MONTH_LABELS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
const DRIVER_MARKER_FALLBACK =
    'data:image/svg+xml;utf8,' +
    encodeURIComponent(
        '<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 28 28">' +
        '<circle cx="14" cy="14" r="11" fill="#6411ED" stroke="#FBFAFE" stroke-width="3"/>' +
        '</svg>'
    );

const KPI_VARIANTS = {
    primary: {
        background: `linear-gradient(105deg, ${C2.violet} 0%, ${C2.bright} 55%, #5B0FD9 100%)`,
        shadow: '0 12px 28px rgba(100,17,237,0.28)',
        text: C2.white,
        sub: 'rgba(251,250,254,0.85)',
        tile: 'rgba(251,250,254,0.16)',
        tileBorder: 'rgba(251,250,254,0.28)',
    },
    deep: {
        background: `linear-gradient(105deg, ${C2.deep} 0%, ${C2.mid} 100%)`,
        shadow: '0 12px 28px rgba(26,15,59,0.28)',
        text: C2.white,
        sub: 'rgba(251,250,254,0.8)',
        tile: 'rgba(251,250,254,0.12)',
        tileBorder: 'rgba(251,250,254,0.22)',
    },
    light: {
        background: C2.white,
        shadow: '0 8px 24px rgba(26,15,59,0.08)',
        text: C2.sidebar,
        sub: C2.muted,
        tile: C2.lavender,
        tileBorder: C2.border,
        border: `1px solid ${C2.border}`,
        iconColor: C2.violet,
    },
};

function KpiCard({ variant = 'primary', icon: Icon, label, value, caption, action }) {
    const v = KPI_VARIANTS[variant];
    return (
        <Box
            sx={{
                height: '100%',
                borderRadius: '18px',
                background: v.background,
                border: v.border || 'none',
                boxShadow: v.shadow,
                px: 2.5,
                py: 2.25,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
            }}
        >
            <Box
                sx={{
                    width: 52,
                    height: 52,
                    flexShrink: 0,
                    borderRadius: '14px',
                    backgroundColor: v.tile,
                    border: `1px solid ${v.tileBorder}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Icon sx={{ color: v.iconColor || v.text, fontSize: 28 }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.85rem', color: v.sub, mb: 0.25 }}>
                    {label}
                </Typography>
                <Typography
                    sx={{
                        fontFamily: C2_FONT,
                        fontWeight: 800,
                        fontSize: { xs: '1.45rem', md: '1.6rem' },
                        color: v.text,
                        lineHeight: 1.15,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}
                >
                    {value}
                </Typography>
                {caption ? (
                    <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.75rem', color: v.sub, mt: 0.25 }}>
                        {caption}
                    </Typography>
                ) : null}
            </Box>
            {action ? <Box sx={{ ml: 'auto', alignSelf: 'flex-start' }}>{action}</Box> : null}
        </Box>
    );
}

function StatRow({ icon: Icon, label, value, accent = C2.violet }) {
    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.75,
                p: 2,
                borderRadius: '14px',
                backgroundColor: C2.white,
                border: `1px solid ${C2.border}`,
            }}
        >
            <Box
                sx={{
                    width: 44,
                    height: 44,
                    flexShrink: 0,
                    borderRadius: '12px',
                    backgroundColor: C2.lavender,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Icon sx={{ color: accent, fontSize: 24 }} />
            </Box>
            <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.82rem', color: C2.muted }}>
                    {label}
                </Typography>
                <Typography sx={{ fontFamily: C2_FONT, fontWeight: 800, fontSize: '1.45rem', color: C2.sidebar, lineHeight: 1.15 }}>
                    {value}
                </Typography>
            </Box>
        </Box>
    );
}

function SectionCard({ icon: Icon, title, action, children, sx }) {
    return (
        <Box
            sx={{
                backgroundColor: C2.white,
                borderRadius: '18px',
                border: `1px solid ${C2.border}`,
                boxShadow: '0 8px 24px rgba(26,15,59,0.06)',
                overflow: 'hidden',
                ...sx,
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1.5,
                    px: 2.5,
                    py: 1.75,
                    borderBottom: `1px solid ${C2.lavender}`,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                    <Icon sx={{ color: C2.violet, fontSize: 22 }} />
                    <Typography
                        sx={{
                            fontFamily: C2_FONT,
                            fontWeight: 800,
                            fontSize: '1rem',
                            letterSpacing: '0.04em',
                            textTransform: 'uppercase',
                            color: C2.sidebar,
                        }}
                    >
                        {title}
                    </Typography>
                </Box>
                {action}
            </Box>
            {children}
        </Box>
    );
}

function SectionTitle({ children, isRTL }) {
    return (
        <Typography
            sx={{
                fontFamily: C2_FONT,
                fontWeight: 800,
                fontSize: { xs: '1.15rem', sm: '1.3rem' },
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: C2.sidebar,
                mb: 1.5,
                textAlign: isRTL === 'rtl' ? 'right' : 'left',
            }}
        >
            {children}
        </Typography>
    );
}

const Dashboard = () => {
    const [mylocation, setMylocation] = useState(null);
    const [locations, setLocations] = useState([]);
    const [selectedMarkerId, setSelectedMarkerId] = useState(null);
    const [dailygross, setDailygross] = useState(0);
    const [monthlygross, setMonthlygross] = useState(0);
    const [totalgross, setTotalgross] = useState(0);

    const [settings, setSettings] = useState({});
    const { i18n } = useTranslation();
    const isRTL = i18n.dir();
    const usersdata = useSelector(state => state.usersdata);
    const bookinglistdata = useSelector(state => state.bookinglistdata);
    const settingsdata = useSelector(state => state.settingsdata);
    const auth = useSelector(state => state.auth);
    const cars = useSelector(state => state.cartypes.cars);
    const [allCompleteCount, setAllCompleteCount] = useState([]);
    const [allCancelCount, setAllCancelCount] = useState([]);
    const [activeCount, setActiveCount] = useState(0);
    const [inServiceCount, setInServiceCount] = useState(0);
    const [resetOpen, setResetOpen] = useState(false);
    const [resetting, setResetting] = useState(false);
    const [driverCount, setDriverCount] = useState(0);
    const [customerCount, setCustomerCount] = useState(0);
    const dispatch = useDispatch();
    const {fetchDrivers, clearFetchDrivers} = api;

    useEffect(() => {
        dispatch(fetchDrivers('web'));
        return () => {
            dispatch(clearFetchDrivers());
        };
    }, [dispatch, fetchDrivers, clearFetchDrivers]);

    useEffect(() => {
        if (mylocation == null) {
            navigator.geolocation.getCurrentPosition(
                position => setMylocation({
                    lat: position.coords.latitude,
                    lng: position.coords.longitude
                }),
                error => console.log(error)
            );
        }
    }, [mylocation]);

    useEffect(() => {
        if (settingsdata.settings) {
            setSettings(settingsdata.settings);
        }
    }, [settingsdata.settings]);

    useEffect(() => {
        if (usersdata.drivers && bookinglistdata.bookings) {
            const liveBookings = bookinglistdata.bookings.filter(bkg => bkg.status === 'STARTED');
            const drivers = usersdata.drivers;
            let locs = [];
           
                for (let i = 0; i < drivers.length; i++) {
                    if (drivers[i].location) {
                        let carImage = DRIVER_MARKER_FALLBACK;
                        let bookingRef = null;
                        for (let j = 0; j < cars.length; j++) {
                            if (cars[j].name === drivers[i].carType) {
                                carImage = cars[j].image || DRIVER_MARKER_FALLBACK;
                            }
                        }
                        for(let j = 0; j<liveBookings.length; j++){
                            if(liveBookings[j].driver === drivers[i].uid){
                                bookingRef = liveBookings[j].reference;
                            }
                        }
                        locs.push({
                            id: i,
                            lat: drivers[i].location.lat,
                            lng: drivers[i].location.lng,
                            drivername: drivers[i].firstName + ' ' + drivers[i].lastName,
                            carnumber: drivers[i].vehicleNumber,
                            cartype: drivers[i].carType,
                            carImage: carImage,
                            bookingRef: bookingRef,
                            fleetadmin:drivers[i].fleetadmin
                        });
                    }
                }
                if (auth.profile.usertype === 'fleetadmin') {
                    locs = locs.filter(driver => driver.fleetadmin === auth.profile.uid);
                }
                setLocations(locs);
                
        }
    }, [usersdata.drivers, auth.profile.usertype, auth.profile.uid, cars, bookinglistdata.bookings, settings]);
    useEffect(() => {
        if (bookinglistdata.bookings) {
            let today = new Date();
            let monthlyTotal = 0;
            let yearlyTotal = 0;
            let todayTotal = 0;
            const accumulatedSince = Number(settings && settings.dashboardEarningsSince) || 0;
            for (let i = 0; i < bookinglistdata.bookings.length; i++) {
                if ((bookinglistdata.bookings[i].status === 'PAID' || bookinglistdata.bookings[i].status === 'COMPLETE') && ((bookinglistdata.bookings[i].fleetadmin === auth.profile.uid && auth.profile.usertype === 'fleetadmin')|| auth.profile.usertype === 'admin') ) {
                    const { tripdate, convenience_fees, fleetCommission, discount } = bookinglistdata.bookings[i];
                    let tDate = new Date(tripdate);
                    if (convenience_fees && parseFloat(convenience_fees) > 0 && auth.profile.usertype === 'admin') {
                        if (tDate.getDate() === today.getDate() && tDate.getMonth() === today.getMonth() && tDate.getFullYear() === today.getFullYear()) {
                            todayTotal = todayTotal + parseFloat(convenience_fees) - (parseFloat(discount) || 0);
                        }
                        if (tDate.getMonth() === today.getMonth() && tDate.getFullYear() === today.getFullYear()) {
                            monthlyTotal = monthlyTotal + parseFloat(convenience_fees) - (parseFloat(discount) || 0);
                        }
                        if (!accumulatedSince || tDate.getTime() >= accumulatedSince) {
                            yearlyTotal = yearlyTotal + parseFloat(convenience_fees) - (parseFloat(discount) || 0);
                        }
                    }
                    if (fleetCommission && parseFloat(fleetCommission) > 0 && auth.profile.usertype === 'fleetadmin'){
                        if (tDate.getDate() === today.getDate() && tDate.getMonth() === today.getMonth() && tDate.getFullYear() === today.getFullYear()) {
                            todayTotal = todayTotal + parseFloat(fleetCommission);
                        }
                        if (tDate.getMonth() === today.getMonth() && tDate.getFullYear() === today.getFullYear()) {
                            monthlyTotal = monthlyTotal + parseFloat(fleetCommission);
                        }
                        yearlyTotal = yearlyTotal + parseFloat(fleetCommission);
                    }
                }
            }
            setDailygross(parseFloat(todayTotal).toFixed(settings.decimal));
            setMonthlygross(parseFloat(monthlyTotal).toFixed(settings.decimal));
            setTotalgross(parseFloat(yearlyTotal).toFixed(settings.decimal));
        }
    }, [bookinglistdata.bookings, settings,auth.profile.uid,auth.profile.usertype]);

    const chartYear = new Date().getFullYear();
    const barChartOptions = {
        labels: MONTH_LABELS,
        colors: [C2.violet, C2.muted],
        chart: {
            type: 'area',
            fontFamily: C2_FONT,
            toolbar: { show: false },
            zoom: { enabled: false },
        },
        dataLabels: { enabled: false },
        stroke: { curve: 'smooth', width: [3, 2] },
        fill: {
            type: 'gradient',
            gradient: { shadeIntensity: 1, opacityFrom: 0.35, opacityTo: 0.02, stops: [0, 90, 100] },
        },
        grid: { borderColor: C2.lavender, strokeDashArray: 4 },
        xaxis: {
            axisBorder: { show: false },
            axisTicks: { show: false },
            labels: { style: { fontFamily: C2_FONT, colors: C2.muted } },
        },
        yaxis: {
            min: 0,
            forceNiceScale: true,
            labels: {
                style: { fontFamily: C2_FONT, colors: C2.muted },
                formatter: (val) => Math.round(val),
            },
        },
        tooltip: { style: { fontFamily: C2_FONT } },
        legend: {
            position: 'top',
            horizontalAlign: 'right',
            fontFamily: C2_FONT,
            labels: { colors: C2.sidebar },
            markers: { radius: 12 },
        },
    };

    const barChart = [{
        name: 'Viajes completados',
        data: allCompleteCount
    }, {
        name: 'Viajes cancelados',
        data: allCancelCount
    }];
    const chartHasData =
        allCompleteCount.some((n) => n > 0) || allCancelCount.some((n) => n > 0);
    useEffect(() => {
        if (usersdata.users) {
            let driverCount = 0;
            let activeCount = 0;
            let inServiceCount = 0;
            let customerCount = 0;
            for (let i = 0; i < usersdata.users.length; i++) {
                if (usersdata.users[i].usertype === 'driver') {
                    if ((auth.profile.usertype === 'fleetadmin' && usersdata.users[i].fleetadmin === auth.profile.uid) || auth.profile.usertype === 'admin') {
                        driverCount = driverCount + 1;
                        if (usersdata.users[i].driverActiveStatus === true) {
                            activeCount = activeCount + 1;
                        }
                        if (usersdata.users[i].queue === true) {
                            inServiceCount = inServiceCount + 1;
                        }
                    }
                }
                if (usersdata.users[i].usertype === 'customer') {
                    if ((auth.profile.usertype === 'fleetadmin' && usersdata.users[i].fleetadmin === auth.profile.uid) || auth.profile.usertype === 'admin') {
                        customerCount = customerCount + 1;
                    }
                }
            }
            setActiveCount(activeCount);
            setInServiceCount(inServiceCount);
            setDriverCount(driverCount);
            setCustomerCount(customerCount);
        } else {
            setActiveCount(0)
            setInServiceCount(0)
            setDriverCount(0)
            setCustomerCount(0)
        }
    }, [usersdata.users, auth.profile]);

    useEffect(() => {
        const completeByMonth = new Array(12).fill(0);
        const cancelByMonth = new Array(12).fill(0);
        const year = new Date().getFullYear();
        if (bookinglistdata.bookings) {
            for (let j = 0; j < bookinglistdata.bookings.length; j++) {
                const { tripdate, status } = bookinglistdata.bookings[j];
                const tDate = new Date(tripdate);
                if (isNaN(tDate.getTime()) || tDate.getFullYear() !== year) continue;
                const m = tDate.getMonth();
                if (status === 'PAID' || status === 'COMPLETE') {
                    completeByMonth[m] += 1;
                } else if (status === 'CANCELLED') {
                    cancelByMonth[m] += 1;
                }
            }
        }
        setAllCompleteCount(completeByMonth);
        setAllCancelCount(cancelByMonth);
    }, [bookinglistdata.bookings]);

    const money = (value) =>
        settings.swipe_symbol === false
            ? formatMoney(value, settings)
            : formatMoney(value, settings, { isRTL: true });
    const mapCenter = mylocation && mylocation.lat ? mylocation : DEFAULT_MAP_CENTER;
    const isFleet = auth.profile?.usertype === 'fleetadmin';
    const earningsCaption = isFleet ? 'Comisión de flota' : 'Tarifa de servicio neta';
    const selectedMarker = locations.find((m) => m.id === selectedMarkerId) || null;
    const isAdmin = auth.profile?.usertype === 'admin';
    const accumulatedSince = isAdmin ? Number(settings.dashboardEarningsSince) || 0 : 0;
    const accumulatedCaption = accumulatedSince
        ? `Desde el ${new Date(accumulatedSince).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' })}`
        : 'Desde el inicio';

    const confirmResetAccumulated = async () => {
        setResetting(true);
        try {
            await api.resetDashboardEarnings();
            setResetOpen(false);
        } catch (e) {
            alert('No se pudo reiniciar el acumulado. Intenta de nuevo.');
        } finally {
            setResetting(false);
        }
    };

    return (
        bookinglistdata.loading || usersdata.loading ? <CircularLoading /> :
            <Box sx={{ px: { xs: 0.5, sm: 1 }, pb: 3, direction: isRTL === 'rtl' ? 'rtl' : 'ltr' }}>
                <SectionTitle isRTL={isRTL}>Ingresos</SectionTitle>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={4}>
                        <KpiCard variant="primary" icon={TodayOutlinedIcon} label="Hoy" value={money(dailygross)} caption={earningsCaption} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <KpiCard variant="deep" icon={CalendarMonthOutlinedIcon} label="Este mes" value={money(monthlygross)} caption={earningsCaption} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <KpiCard
                            variant="light"
                            icon={AccountBalanceWalletOutlinedIcon}
                            label="Acumulado"
                            value={money(totalgross)}
                            caption={accumulatedCaption}
                            action={isAdmin ? (
                                <Tooltip title="Reiniciar acumulado">
                                    <IconButton size="small" onClick={() => setResetOpen(true)} sx={{ color: C2.muted, '&:hover': { color: C2.violet, backgroundColor: C2.lavender } }}>
                                        <RestartAltRoundedIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                            ) : null}
                        />
                    </Grid>
                </Grid>

                <C2Dialog
                    open={resetOpen}
                    onClose={() => !resetting && setResetOpen(false)}
                    title="Reiniciar acumulado"
                    icon={RestartAltRoundedIcon}
                    actions={
                        <>
                            <C2SecondaryButton onClick={() => setResetOpen(false)} disabled={resetting}>
                                Cancelar
                            </C2SecondaryButton>
                            <C2DangerButton onClick={confirmResetAccumulated} disabled={resetting}>
                                {resetting ? 'Reiniciando…' : 'Reiniciar a cero'}
                            </C2DangerButton>
                        </>
                    }
                >
                    <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.92rem', color: C2.muted }}>
                        El acumulado empezará a contar desde este momento. Solo cambia la cifra del dashboard:
                        no se borra ningún viaje ni cambian los reportes de ganancias.
                    </Typography>
                </C2Dialog>

                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12} md={8} lg={9}>
                        <SectionCard
                            icon={MapOutlinedIcon}
                            title="Conductores en tiempo real"
                            action={
                                <Box
                                    sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 0.75,
                                        px: 1.25,
                                        py: 0.4,
                                        borderRadius: '999px',
                                        backgroundColor: C2.lavender,
                                        border: `1px solid ${C2.border}`,
                                        flexShrink: 0,
                                    }}
                                >
                                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: locations.length ? '#22C55E' : C2.muted }} />
                                    <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.78rem', fontWeight: 600, color: C2.deep }}>
                                        {locations.length} en el mapa
                                    </Typography>
                                </Box>
                            }
                        >
                            <Box sx={{ position: 'relative' }}>
                                <GoogleMap
                                    zoom={mylocation ? 11 : 6}
                                    center={mapCenter}
                                    mapContainerStyle={{ height: '400px' }}
                                    options={{ streetViewControl: false, mapTypeControl: false, fullscreenControl: true }}
                                    onClick={() => setSelectedMarkerId(null)}
                                >
                                    {locations.map(marker => (
                                        <Marker
                                            position={{ lat: marker.lat, lng: marker.lng }}
                                            key={marker.id}
                                            onClick={() => setSelectedMarkerId(marker.id)}
                                            icon={{
                                                url: marker.carImage,
                                                scaledSize: marker.carImage === DRIVER_MARKER_FALLBACK
                                                    ? new window.google.maps.Size(22, 22)
                                                    : new window.google.maps.Size(35, 25)
                                            }}
                                        />
                                    ))}
                                    {selectedMarker ? (
                                        <InfoWindow
                                            position={{ lat: selectedMarker.lat, lng: selectedMarker.lng }}
                                            options={{ pixelOffset: new window.google.maps.Size(0, -18) }}
                                            onCloseClick={() => setSelectedMarkerId(null)}
                                        >
                                            <Box sx={{ fontFamily: C2_FONT, minWidth: 140 }}>
                                                <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, fontSize: '0.9rem', color: C2.sidebar }}>
                                                    {selectedMarker.drivername}
                                                </Typography>
                                                <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.8rem', color: C2.muted }}>
                                                    {[selectedMarker.cartype, selectedMarker.carnumber].filter(Boolean).join(' · ') || 'Sin vehículo'}
                                                </Typography>
                                                {selectedMarker.bookingRef ? (
                                                    <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.78rem', fontWeight: 700, color: C2.violet, mt: 0.5 }}>
                                                        En viaje · {selectedMarker.bookingRef}
                                                    </Typography>
                                                ) : null}
                                            </Box>
                                        </InfoWindow>
                                    ) : null}
                                </GoogleMap>
                                {locations.length === 0 ? (
                                    <Box
                                        sx={{
                                            position: 'absolute',
                                            left: '50%',
                                            bottom: 20,
                                            transform: 'translateX(-50%)',
                                            px: 2,
                                            py: 1,
                                            borderRadius: '12px',
                                            backgroundColor: 'rgba(26,15,59,0.88)',
                                            boxShadow: '0 8px 20px rgba(26,15,59,0.3)',
                                            pointerEvents: 'none',
                                        }}
                                    >
                                        <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.85rem', color: C2.white, textAlign: 'center' }}>
                                            No hay conductores con ubicación en este momento.
                                        </Typography>
                                    </Box>
                                ) : null}
                            </Box>
                        </SectionCard>
                    </Grid>
                    <Grid item xs={12} md={4} lg={3}>
                        <Box
                            sx={{
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 1.5,
                                p: 2,
                                borderRadius: '18px',
                                background: `linear-gradient(160deg, ${C2.sidebar} 0%, ${C2.deep} 100%)`,
                                boxShadow: '0 12px 28px rgba(26,15,59,0.25)',
                            }}
                        >
                            <Typography
                                sx={{
                                    fontFamily: C2_FONT,
                                    fontWeight: 800,
                                    fontSize: '0.95rem',
                                    letterSpacing: '0.04em',
                                    textTransform: 'uppercase',
                                    color: C2.white,
                                    px: 0.5,
                                }}
                            >
                                Comunidad
                            </Typography>
                            <StatRow icon={PeopleAltOutlinedIcon} label="Usuarios registrados" value={customerCount} />
                            <StatRow icon={DirectionsCarOutlinedIcon} label="Conductores registrados" value={driverCount} />
                            <StatRow icon={WifiTetheringOutlinedIcon} label="Conductores activos" value={activeCount} accent="#16A34A" />
                            <StatRow icon={NavigationOutlinedIcon} label="Conductores en servicio" value={inServiceCount} accent="#D97706" />
                            <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.8rem', color: 'rgba(251,250,254,0.7)', mt: 'auto', px: 0.5 }}>
                                Juntos llegamos más lejos.
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>

                <SectionCard
                    icon={InsertChartOutlinedIcon}
                    title="Viajes por mes"
                    action={
                        <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.85rem', fontWeight: 600, color: C2.muted }}>
                            Año {chartYear}
                        </Typography>
                    }
                >
                    <Box sx={{ px: { xs: 1, sm: 2 }, pt: 1, pb: 2, position: 'relative' }}>
                        <Chart
                            options={barChartOptions}
                            series={barChart}
                            type="area"
                            width="100%"
                            height={340}
                        />
                        {!chartHasData ? (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    pointerEvents: 'none',
                                }}
                            >
                                <Typography sx={{ fontFamily: C2_FONT, fontWeight: 700, color: C2.sidebar }}>
                                    Aún no hay viajes este año
                                </Typography>
                                <Typography sx={{ fontFamily: C2_FONT, fontSize: '0.85rem', color: C2.muted }}>
                                    Cuando se completen reservas en Conecta2 las verás aquí.
                                </Typography>
                            </Box>
                        ) : null}
                    </Box>
                </SectionCard>
            </Box>

    )
}

export default Dashboard;